import React, { useEffect, useMemo, useState } from "react";
import CommonFreeTextInput from "../../common/inputs/CommonFreeTextInput";
import { formatMoney, getNumber } from "../../../features/utils/formatMoney";
import CommonCollapsible from "../../common/CommonCollapsible";
import { clone } from "../../../features/utils/objClone";

export default function PassengerBreakdownCard({
  normalized,
  serviceFee,
  passengerCount,
  item,
  travellerLabel,
  getSubtotalForItem,
  travelerType,
  handleSaveBreakdown,
  defData,
}) {
  const [open, setOpen] = useState(false);
  const [localBreakdown, setLocalBreakdown] = useState([]);

  useEffect(() => {
    // if (!open) return;
    setLocalBreakdown(item?.breakDown || []);
    // eslint-disable-next-line
  }, [open, item?.travelerType]);

  const globalBreakdown = useMemo(() => item?.breakDown || [], [item]);

  const hasGlobalQuoteValue = useMemo(() => {
    return (globalBreakdown || []).some((x) => Number(x?.amount || 0) > 0);
  }, [globalBreakdown]);

  const isDirty = useMemo(() => {
    const g = globalBreakdown || [];
    const l = localBreakdown || [];
    if (g.length !== l.length) return true;

    for (let i = 0; i < g.length; i++) {
      const ga = Number(g[i]?.amount || 0);
      const la = Number(l[i]?.amount || 0);
      if (ga !== la) return true;
    }
    return false;
  }, [globalBreakdown, localBreakdown]);

  const icon = useMemo(() => {
    switch (travelerType) {
      case "CHD":
      case "CNN":
        return "./flightChild.svg";
      case "INF":
      case "INS":
        return "./flightInfant.svg";
      default:
        return "./flightAdult.svg";
    }
  }, [travelerType]);

  // --------------------------------
  // Breakdown logic
  // --------------------------------

  // deducted breakdown PER PAX (local)
  const localDeductedTotal = useMemo(() => {
    return (localBreakdown || []).reduce((acc, cur) => {
      if (!cur?.isDeducted) return acc;
      return acc + Number(cur?.amount || 0);
    }, 0);
  }, [localBreakdown]);

  const travellerMap = useMemo(
    () => ({
      ADT: "ADT",
      Adult: "ADT",
      CHD: "CHD",
      CNN: "CHD",
      Child: "CHD",
      INS: "INF",
      INF: "INF",
      Infant: "INF",
    }),
    [],
  );

  const passengerIds = useMemo(() => defData?.passengerIds || [], [defData]);

  // unit airline price PER PAX
  const unitPrice = useMemo(() => {
    const travellers = defData?.flightObj?.travelers || [];

    const selected = passengerIds
      .map((id) => travellers.find((t) => t?.id === id))
      .filter(Boolean)
      .filter(
        (p) =>
          (travellerMap[p?.travelerType] || p?.travelerType) ===
          (travellerMap[travelerType] || travelerType),
      );

    if (!selected.length) return 0;

    const travelerDict = {
      ADT: defData?.flightObj?.adult || {},
      CHD: defData?.flightObj?.child || {},
      CNN: defData?.flightObj?.child || {},
      INF: defData?.flightObj?.infant || {},
      INS: defData?.flightObj?.infant || {},
    };

    const pricing = travelerDict[travelerType] || {};
    // console.log({ pricing });

    const total = (() => {
      if (typeof pricing?.totalPrice !== "string")
        return Number(pricing?.totalPrice || 0);

      const numeric = parseFloat(
        pricing.totalPrice.replace(/^[A-Za-z\s]+/, "").replaceAll(",", ""),
      );
      return Number.isFinite(numeric) ? numeric : 0;
    })();

    return total / (pricing?.passengerCount || passengerCount || 1);
    // return total;
  }, [travelerType, defData, passengerIds, travellerMap, passengerCount]);

  // --------------------------------
  // ✅ CORRECT refund math (based on LOCAL while editing)
  // --------------------------------
  const refundPerPax = useMemo(() => {
    return Math.max(
      0,
      Number(unitPrice || 0) -
        Number(localDeductedTotal || 0) -
        Number(serviceFee || 0),
    );
  }, [unitPrice, localDeductedTotal, serviceFee]);

  const refundTotal = useMemo(() => {
    return refundPerPax * Number(passengerCount || 0);
  }, [refundPerPax, passengerCount]);

  // console.log({ serviceFee });

  // --------------------------------
  // Input handling (✅ capped so deducted sum <= unitPrice)
  // --------------------------------
  const onChangeLocal = (i, v) => {
    const next = clone(localBreakdown || []);
    if (!next?.[i]) return;

    let val = Math.max(0, Number(v || 0));

    if (next[i]?.isDeducted) {
      const otherDeducted = next.reduce((acc, cur, idx) => {
        if (idx === i) return acc;
        if (!cur?.isDeducted) return acc;
        return acc + Number(cur?.amount || 0);
      }, 0);

      const maxAllowed = Math.max(0, Number(unitPrice || 0) - otherDeducted);
      val = Math.min(val, maxAllowed);
    }

    next[i].amount = val;
    setLocalBreakdown(next);
  };

  const onSave = () => {
    if (!isDirty) {
      setOpen(false);
      return;
    }
    handleSaveBreakdown?.(item?.travelerType, localBreakdown);
    setOpen(false);
  };

  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-4 flex flex-col">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setOpen((s) => !s)}
      >
        <div className="font-semibold text-gray-900 flex items-center gap-1 ">
          <img
            src={icon}
            alt=""
            className={
              "w-8 h-8 object-contain mr-4 " +
              (icon === "./flightChild.svg" ? "mb-1" : "")
            }
          />
          <span>{travellerLabel?.[normalized] || normalized}</span>
          <div className="text-xs text-gray-500 font-medium">
            (x{passengerCount})
          </div>
        </div>

        {!hasGlobalQuoteValue ? (
          <button
            type="button"
            className="w-[150px] rounded-md border border-primary1 text-primary1 flex items-center justify-center h-10 text-sm font-semibold"
          >
            Add Quote
          </button>
        ) : (
          // ✅ was "Breakdown total" — now show refund total
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-2 flex items-center gap-3">
            <div className="text-sm text-gray-600">Refund total</div>
            <div className="font-semibold text-gray-900">
              {formatMoney(refundTotal)}
            </div>
          </div>
        )}
      </div>

      {/* Collapsible */}
      <CommonCollapsible open={open}>
        <div className="w-full flex flex-col pt-4">
          <div className="space-y-3 border-t border-gray-100 pt-4">
            {(localBreakdown || []).map((obj, i) => (
              <div key={i} className="flex items-start justify-between gap-4">
                <div className="flex flex-col">
                  <div className="text-sm font-medium text-gray-800">
                    {obj?.name}
                    <span className="text-xs text-gray-400 ml-1">
                      (per PAX)
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {obj?.isDeducted ? "Deducted" : "Informational"}
                  </div>
                </div>

                <div className="w-[190px]">
                  <CommonFreeTextInput
                    label=""
                    placeholder="0"
                    value={String(obj?.amount?.toLocaleString() ?? "")}
                    onChange={(v) => onChangeLocal(i, getNumber(v))}
                    className="!p-0"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Service charge */}
          <div className="mt-4 pt-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">Service Charge </div>
            <div className="font-semibold text-gray-900">
              {formatMoney(serviceFee)}
            </div>
          </div>

          {/* Fixed values */}
          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <div className="text-sm text-gray-600">
              Total refund per {travellerLabel?.[normalized] || normalized}
            </div>
            <div className="font-semibold text-gray-900">
              {formatMoney(refundPerPax)}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <div className="text-sm text-black font-bold">
              Total refund{" "}
              <span className="text-xs text-gray-400">
                (x{passengerCount} {travellerLabel?.[normalized] || normalized})
              </span>
            </div>
            <div className="font-semibold text-gray-900">
              {formatMoney(refundTotal)}
            </div>
          </div>

          {/* Save */}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!isDirty}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition
                ${
                  isDirty
                    ? "bg-primary1 text-white hover:opacity-90"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
            >
              Save
            </button>
          </div>
        </div>
      </CommonCollapsible>
    </div>
  );
}
